import { getSupabaseWithLogging } from "@/lib/hooks/api/utils";
import { buildIlikeOrFilter } from "@west-tennessee-consulting/supabase/postgrest-filter";

/**
 * Shared select fragment for contact_tasks queries that include
 * contact, owning user, and task notes (with the note author).
 */
export const TASK_WITH_RELATIONS_SELECT = `
  *,
  contacts:contact_id (
      id,
      name,
      company_name,
      email
  ),
  users:app_users!contact_tasks_user_id_fkey(
      id,
      first_name,
      last_name,
      email
  ),
  task_notes(
      *,
      user:app_users!task_notes_user_id_fkey(
          id,
          first_name,
          last_name,
          email
      )
  )
`;

/**
 * Subset of contact_tasks select that omits task_notes — used by the
 * single-task fetcher where notes are loaded separately.
 */
export const TASK_WITH_CONTACT_AND_USER_SELECT = `
  *,
  contacts:contact_id (
      id,
      name,
      company_name,
      email
  ),
  users:app_users!contact_tasks_user_id_fkey(
      id,
      first_name,
      last_name,
      email
  )
`;

export interface TaskListFilters {
  contact_id?: string;
  ticket_id?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  assigned_users?: string[];
  user_id?: string;
  search?: string;
}

/**
 * Applies the standard set of contact_tasks filters to a supabase query
 * builder. Returns the (possibly re-typed) chained query.
 */
export function applyTaskFilters<Q extends { [key: string]: any }>(
  query: Q,
  params: TaskListFilters | undefined,
  options: { includeTicketId?: boolean } = {}
): Q {
  if (!params) return query;
  let q: any = query;
  if (params.contact_id) {
    q = q.eq("contact_id", params.contact_id);
  }
  if (options.includeTicketId && params.ticket_id) {
    q = q.eq("ticket_id", params.ticket_id);
  }
  if (params.status) {
    q = q.eq("status", params.status);
  }
  if (params.priority) {
    q = q.eq("priority", params.priority);
  }
  if (params.assigned_to) {
    q = q.eq("assigned_to", params.assigned_to);
  }
  if (params.assigned_users && params.assigned_users.length > 0) {
    q = q.overlaps("assigned_users", params.assigned_users);
  }
  if (params.user_id) {
    q = q.eq("user_id", params.user_id);
  }
  if (params.search) {
    // User text is sanitized: commas/parens would otherwise rewrite the PostgREST filter.
    const searchFilter = buildIlikeOrFilter(["title", "description"], params.search);
    if (searchFilter) q = q.or(searchFilter);
  }
  return q as Q;
}

/**
 * Replaces the string user IDs inside each task's `assigned_users` array
 * with the matching `app_users` rows. Mutates the rows in place; errors
 * are logged and the original IDs are left untouched.
 */
export async function hydrateAssignedUsersForTasks(data: any[]): Promise<void> {
  if (!data || data.length === 0) return;

  const allUserIds = new Set<string>();
  data.forEach((task) => {
    if (task.assigned_users && Array.isArray(task.assigned_users)) {
      task.assigned_users.forEach((userId: string) => {
        if (userId) allUserIds.add(userId);
      });
    }
  });

  if (allUserIds.size === 0) return;

  const supabase = getSupabaseWithLogging();
  const { data: userData, error: userError } = await supabase
    .from("app_users")
    .select("id, first_name, last_name, email")
    .in("id", Array.from(allUserIds));

  if (userError) {
    console.error("Error fetching user data:", userError);
    return;
  }
  if (!userData) return;

  const userMap = new Map(userData.map((user) => [user.id, user]));
  data.forEach((task) => {
    if (task.assigned_users && Array.isArray(task.assigned_users)) {
      task.assigned_users = task.assigned_users.map(
        (userId: string) => userMap.get(userId) || userId
      );
    }
  });
}

/**
 * Same as {@link hydrateAssignedUsersForTasks} but for a single task row.
 * Filters out non-string / empty IDs before querying.
 */
export async function hydrateAssignedUsersForTask(data: any): Promise<void> {
  if (
    !data ||
    !data.assigned_users ||
    !Array.isArray(data.assigned_users) ||
    data.assigned_users.length === 0
  ) {
    return;
  }

  const validUserIds = data.assigned_users.filter(
    (userId: any) => typeof userId === "string" && userId.trim() !== ""
  );

  if (validUserIds.length === 0) return;

  const supabase = getSupabaseWithLogging();
  const { data: userData, error: userError } = await supabase
    .from("app_users")
    .select("id, first_name, last_name, email")
    .in("id", validUserIds);

  if (userError) {
    console.error("Error fetching user data:", userError);
    return;
  }
  if (!userData) return;

  const userMap = new Map(userData.map((user) => [user.id, user]));
  data.assigned_users = data.assigned_users.map(
    (userId: string) => userMap.get(userId) || userId
  );
}
